import React, { useState, useEffect } from 'react';
import './QuickInquiry.css';

/**
 * Quick Inquiry Widget — Per-product WhatsApp pre-filled inquiry.
 *
 * Renders:
 *   1. A floating sticky CTA on the right edge (desktop) / bottom-bar (mobile)
 *   2. A modal with quantity / country / company / notes fields
 *   3. On submit, opens WhatsApp with a pre-filled product-specific message
 *
 * Props:
 *   productName  - display name (e.g. "Ashwagandha Root Powder")
 *   botanical    - botanical name (optional, included in message if provided)
 */

const WHATSAPP_PHONE = "916267592923";
const DEFAULT_PACKAGING = "25 kg HDPE-lined export bags (food-grade)";

function buildWhatsAppMessage({ productName, botanical, quantity, country, companyName, notes, packaging }) {
  const lines = [
    "Hello HOPE INTERNATIONAL,",
    "",
    `We are interested in sourcing ${productName}${botanical ? ` (${botanical})` : ""}.`,
    "",
    `Required Quantity: ${quantity || "[please discuss]"}`,
    `Destination Country: ${country || "[please discuss]"}`,
    `Company Name: ${companyName || "[please discuss]"}`,
    `Packaging: ${packaging || DEFAULT_PACKAGING}`,
  ];

  if (notes && notes.trim()) {
    lines.push("", `Additional Notes: ${notes.trim()}`);
  }

  lines.push("", "Please share quotation, specification sheet, and export details.");
  lines.push("", "Thank you.");

  return lines.join("\n");
}

function QuickInquiryWidget({ productName, botanical }) {
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    quantity: "",
    country: "",
    companyName: "",
    notes: "",
  });

  // Lock body scroll when modal open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    const message = buildWhatsAppMessage({
      productName,
      botanical,
      quantity: form.quantity,
      country: form.country,
      companyName: form.companyName,
      notes: form.notes,
      packaging: DEFAULT_PACKAGING,
    });

    const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
    // Open WhatsApp in new tab
    window.open(url, "_blank", "noopener,noreferrer");

    // Slight delay so the user can see the spinner / understand the action
    setTimeout(() => {
      setSubmitting(false);
      setIsOpen(false);
      setForm({ quantity: "", country: "", companyName: "", notes: "" });
    }, 600);
  };

  // Quick "instant" inquiry — just opens WhatsApp with product name pre-filled (no fields)
  const handleInstantInquiry = () => {
    const message = buildWhatsAppMessage({
      productName,
      botanical,
      quantity: "",
      country: "",
      companyName: "",
      notes: "",
      packaging: DEFAULT_PACKAGING,
    });
    const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  return (
    <>
      {/* Sticky right-edge CTA (desktop) */}
      <button
        type="button"
        className="qi-sticky-right"
        onClick={() => setIsOpen(true)}
        aria-label={`Quick Inquiry for ${productName}`}
        data-testid="quick-inquiry-sticky-btn"
      >
        <svg viewBox="0 0 32 32" width="22" height="22" fill="currentColor" aria-hidden="true">
          <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM21.837 18.989c-0.305-0.153-1.796-0.889-2.074-0.991s-0.48-0.153-0.683 0.153c-0.203 0.305-0.784 0.991-0.961 1.194s-0.355 0.229-0.659 0.076c-0.305-0.153-1.283-0.473-2.444-1.513-0.905-0.809-1.515-1.807-1.692-2.112s-0.018-0.47 0.134-0.622c0.137-0.137 0.305-0.355 0.458-0.533s0.203-0.305 0.305-0.508c0.102-0.203 0.051-0.381-0.025-0.533s-0.683-1.645-0.936-2.254c-0.245-0.592-0.495-0.512-0.683-0.522-0.178-0.010-0.381-0.010-0.584-0.010s-0.533 0.076-0.812 0.381c-0.279 0.305-1.065 1.041-1.065 2.54s1.090 2.947 1.243 3.150c0.153 0.203 2.16 3.298 5.231 4.625 0.731 0.315 1.301 0.504 1.746 0.645 0.733 0.229 1.401 0.195 1.929 0.118 0.588-0.089 1.796-0.736 2.048-1.447s0.254-1.320 0.178-1.447c-0.076-0.127-0.279-0.203-0.584-0.355z" />
        </svg>
        <span className="qi-sticky-label">Quick Inquiry</span>
      </button>

      {/* Sticky bottom bar (mobile) */}
      <div className="qi-mobile-bar">
        <div className="qi-mobile-info">
          <span className="qi-mobile-eyebrow">Bulk B2B Supply</span>
          <span className="qi-mobile-product">{productName}</span>
        </div>
        <button
          type="button"
          className="qi-mobile-cta"
          onClick={() => setIsOpen(true)}
          aria-label={`Quick Inquiry for ${productName}`}
          data-testid="quick-inquiry-mobile-btn"
        >
          <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true">
            <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM21.837 18.989c-0.305-0.153-1.796-0.889-2.074-0.991s-0.48-0.153-0.683 0.153c-0.203 0.305-0.784 0.991-0.961 1.194s-0.355 0.229-0.659 0.076c-0.305-0.153-1.283-0.473-2.444-1.513-0.905-0.809-1.515-1.807-1.692-2.112s-0.018-0.47 0.134-0.622c0.137-0.137 0.305-0.355 0.458-0.533s0.203-0.305 0.305-0.508c0.102-0.203 0.051-0.381-0.025-0.533s-0.683-1.645-0.936-2.254c-0.245-0.592-0.495-0.512-0.683-0.522-0.178-0.010-0.381-0.010-0.584-0.010s-0.533 0.076-0.812 0.381c-0.279 0.305-1.065 1.041-1.065 2.54s1.090 2.947 1.243 3.150c0.153 0.203 2.16 3.298 5.231 4.625 0.731 0.315 1.301 0.504 1.746 0.645 0.733 0.229 1.401 0.195 1.929 0.118 0.588-0.089 1.796-0.736 2.048-1.447s0.254-1.320 0.178-1.447c-0.076-0.127-0.279-0.203-0.584-0.355z" />
          </svg>
          Quick Inquiry
        </button>
      </div>

      {/* Modal */}
      {isOpen && (
        <div
          className="qi-modal-overlay"
          onClick={() => !submitting && setIsOpen(false)}
          data-testid="quick-inquiry-modal"
        >
          <div
            className="qi-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qi-modal-title"
          >
            <button
              type="button"
              className="qi-modal-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
              data-testid="quick-inquiry-close-btn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="qi-modal-header">
              <span className="qi-modal-eyebrow">Bulk B2B Inquiry — Direct on WhatsApp</span>
              <h3 id="qi-modal-title">Quick Inquiry: {productName}</h3>
              <p className="qi-modal-sub">
                Tell us your requirement — we'll respond on WhatsApp within business hours with a quote, spec sheet & export details.
              </p>
            </div>

            <form className="qi-form" onSubmit={handleSubmit}>
              <div className="qi-product-tag">
                <span className="qi-tag-label">Product</span>
                <span className="qi-tag-value">{productName}{botanical ? ` · ${botanical}` : ""}</span>
              </div>

              <div className="qi-form-row">
                <label className="qi-field">
                  <span>Required Quantity *</span>
                  <input
                    type="text"
                    name="quantity"
                    value={form.quantity}
                    onChange={handleChange}
                    placeholder="e.g. 500 kg, 1 MT, 1×20ft container"
                    required
                    data-testid="quick-inquiry-quantity"
                  />
                </label>

                <label className="qi-field">
                  <span>Destination Country *</span>
                  <input
                    type="text"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    placeholder="e.g. United States, Germany, UAE"
                    required
                    data-testid="quick-inquiry-country"
                  />
                </label>
              </div>

              <label className="qi-field">
                <span>Buyer Company Name *</span>
                <input
                  type="text"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  placeholder="Your company / brand name"
                  required
                  data-testid="quick-inquiry-company"
                />
              </label>

              <label className="qi-field">
                <span>Additional Notes (optional)</span>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Mesh size, MOQ, target price, sample needed, certifications required, etc."
                  rows="3"
                  data-testid="quick-inquiry-notes"
                />
              </label>

              <div className="qi-packaging-info">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                <span>Standard export packaging: <strong>{DEFAULT_PACKAGING}</strong></span>
              </div>

              <div className="qi-form-actions">
                <button
                  type="button"
                  className="qi-btn-secondary"
                  onClick={handleInstantInquiry}
                  data-testid="quick-inquiry-instant-btn"
                >
                  Skip & Chat Now
                </button>
                <button
                  type="submit"
                  className="qi-btn-primary"
                  disabled={submitting}
                  data-testid="quick-inquiry-submit-btn"
                >
                  {submitting ? (
                    "Opening WhatsApp..."
                  ) : (
                    <>
                      <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true" style={{ marginRight: 8, verticalAlign: "middle" }}>
                        <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM21.837 18.989c-0.305-0.153-1.796-0.889-2.074-0.991s-0.48-0.153-0.683 0.153c-0.203 0.305-0.784 0.991-0.961 1.194s-0.355 0.229-0.659 0.076c-0.305-0.153-1.283-0.473-2.444-1.513-0.905-0.809-1.515-1.807-1.692-2.112s-0.018-0.47 0.134-0.622c0.137-0.137 0.305-0.355 0.458-0.533s0.203-0.305 0.305-0.508c0.102-0.203 0.051-0.381-0.025-0.533s-0.683-1.645-0.936-2.254c-0.245-0.592-0.495-0.512-0.683-0.522-0.178-0.010-0.381-0.010-0.584-0.010s-0.533 0.076-0.812 0.381c-0.279 0.305-1.065 1.041-1.065 2.54s1.090 2.947 1.243 3.150c0.153 0.203 2.16 3.298 5.231 4.625 0.731 0.315 1.301 0.504 1.746 0.645 0.733 0.229 1.401 0.195 1.929 0.118 0.588-0.089 1.796-0.736 2.048-1.447s0.254-1.320 0.178-1.447c-0.076-0.127-0.279-0.203-0.584-0.355z" />
                      </svg>
                      Send via WhatsApp
                    </>
                  )}
                </button>
              </div>

              <p className="qi-privacy-note">
                Your inquiry opens directly in WhatsApp. No data stored on this site.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default QuickInquiryWidget;
